import * as React from 'react'

import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import type { PageViewport, PDFDocumentProxy } from 'pdfjs-dist'
import { classList, toggleClass } from 'react-classlist-helper'
import { Document, Page, pdfjs } from 'react-pdf'

import { customDebounce } from '../../utils/util'
import { Loader } from '../Loader'
import { SpinnerSize } from '../Spinner/Spinner'

let workerInitialized: Promise<void> | null = null

const initializeWorker = (): Promise<void> => {
  if (workerInitialized) {
    return workerInitialized
  }

  workerInitialized = (async () => {
    try {
      const response = await fetch('/pdf.worker.min.mjs')
      if (response.ok) {
        const workerCode = await response.text()
        const workerBlob = new Blob([workerCode], {
          type: 'application/javascript',
        })
        const workerBlobUrl = URL.createObjectURL(workerBlob)
        pdfjs.GlobalWorkerOptions.workerSrc = workerBlobUrl
      } else {
        throw new Error('Local worker not found')
      }
    } catch {
      pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
    }
  })()

  return workerInitialized
}

interface IProps {
  file: string | File

  containerClassName?: string
  className?: string
  loaderSize?: SpinnerSize
  onLoadSuccess?(pdf: any): void

  disableTopMargin?: boolean
  pageNumber?: number
  pageClassName?: string
  isPageHidden?: boolean
  isTextSelectable?: boolean
  scale?: number
  devicePixelRatio?: number
  pageWidth?: number
  pageHeight?: number
  widthMargin?: number
  heightMargin?: number
  shouldFitToWidth?: boolean
  externalContainerEl?: HTMLDivElement
  onPageLoad?(page: any): void
  onPageRenderSuccess?(): void
  setPageRef?(ref: HTMLDivElement, index?: number): void
  shouldRenderAllPages?: boolean
}

const DEFAULT_LOADER_SIZE: SpinnerSize = 'md'
const RESIZE_DEBOUNCE_TIME = 300

// Without `wasmUrl` pdf.js cannot load its OpenJPEG decoder, so every
// JPEG 2000 image on a page fails to decode. That failure is silent — the
// worker resolves the image to null, the canvas skips the draw, and the render
// promise still succeeds — leaving a page rendered without its raster content.
export const documentOptions = {
  cMapUrl: '/cmaps/',
  cMapPacked: true,
  wasmUrl: '/wasm/',
}

@observer
export default class PdfDocument extends React.Component<IProps> {
  @observable private numPages: number = 1
  @observable private isLoading: boolean = true
  @observable private isWorkerReady: boolean = false
  @observable private containerWidth: number = null
  @observable private containerHeight: number = null
  @observable private pageViewport: PageViewport = null
  @observable private localProps: IProps = null

  private containerRef = React.createRef<HTMLDivElement>()

  public static defaultProps = {
    isTextSelectable: false,
  }

  public constructor(props: IProps) {
    super(props)

    this.localProps = props

    this.handleResize = customDebounce(this.handleResize, RESIZE_DEBOUNCE_TIME)

    makeObservable(this)
  }

  public async componentDidMount() {
    await initializeWorker()
    runInAction(() => {
      this.isWorkerReady = true
    })

    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  public render(): JSX.Element {
    const { file, className, children, containerClassName } = this.props

    if (!this.isWorkerReady) {
      return this.loader
    }

    return (
      <div
        ref={this.containerRef}
        className={classList({
          'relative full-height full-width': true,
          [containerClassName]: !!containerClassName,
        })}
      >
        <Document
          file={file}
          className={className}
          loading={this.loader}
          onLoadSuccess={this.onLoadSuccess}
          onLoadError={this.onLoadError}
          options={documentOptions}
        >
          {this.renderPages()}
          {children}
        </Document>
      </div>
    )
  }

  private renderPages(): JSX.Element | JSX.Element[] | null {
    const { shouldRenderAllPages, pageNumber, isPageHidden, disableTopMargin } =
      this.props

    if (isPageHidden) {
      return null
    }

    if (this.isLoading) {
      return this.loader
    }

    if (this.numPages === 0) {
      return null
    }

    if (shouldRenderAllPages) {
      return Array.from({ length: this.numPages }, (_, index) => {
        return (
          <div
            key={`page_${index + 1}`}
            className={toggleClass('mt60', !index && !disableTopMargin)}
          >
            {this.renderPage(index + 1, index)}
          </div>
        )
      })
    }

    const pageToRender = pageNumber || 1
    return this.renderPage(pageToRender)
  }

  private get loader(): JSX.Element {
    return (
      <div className="absolute row x-center y-center full-height full-width">
        <Loader size={this.props.loaderSize || DEFAULT_LOADER_SIZE} />
      </div>
    )
  }

  private onLoadSuccess = async (doc: PDFDocumentProxy): Promise<void> => {
    runInAction(() => {
      this.numPages = doc.numPages
      this.isLoading = false
    })

    if (doc.numPages > 0) {
      const page = await doc.getPage(1)
      const viewport = page.getViewport({ scale: 1 })
      runInAction(() => {
        this.pageViewport = viewport
      })
    }

    this.props.onLoadSuccess?.(doc)
  }

  @action.bound
  private onLoadError(): void {
    this.isLoading = false
  }

  private renderPage(pageNumber: number, index?: number): JSX.Element {
    const {
      setPageRef,
      pageClassName,
      isTextSelectable,
      onPageLoad,
      onPageRenderSuccess,
      devicePixelRatio,
    } = this.props

    return (
      <Page
        inputRef={el => setPageRef?.(el, index)}
        className={pageClassName}
        loading={null}
        onLoadSuccess={onPageLoad}
        onRenderSuccess={onPageRenderSuccess}
        pageNumber={pageNumber}
        scale={this.pageViewportParams.scale}
        width={this.pageViewportParams.width}
        height={this.pageViewportParams.height}
        devicePixelRatio={devicePixelRatio}
        renderAnnotationLayer={isTextSelectable}
        renderTextLayer={isTextSelectable}
      />
    )
  }

  private handleResize = () => {
    const container =
      this.props.externalContainerEl || this.containerRef.current
    if (container) {
      runInAction(() => {
        this.containerWidth = container.clientWidth
        this.containerHeight = container.clientHeight
      })
    }
  }

  @computed
  private get pageViewportParams(): Partial<PageViewport> {
    const {
      pageWidth,
      pageHeight,
      widthMargin,
      heightMargin,
      shouldFitToWidth,
      scale,
    } = this.localProps

    let finalScale: number = scale
    let finalWidth: number = pageWidth
    let finalHeight: number = pageHeight

    const targetHeight = this.containerHeight || pageHeight
    if (this.containerWidth && targetHeight && this.pageViewport) {
      const availableWidth = this.containerWidth - (widthMargin || 0)
      const availableHeight = targetHeight - (heightMargin || 0)
      const scaleX = availableWidth / this.pageViewport.width
      const scaleY = availableHeight / this.pageViewport.height
      const fitScale = shouldFitToWidth ? scaleX : Math.min(scaleX, scaleY)

      finalScale = fitScale * (scale || 1)
      finalWidth = undefined
      finalHeight = undefined
    } else if (pageWidth) {
      finalWidth = pageWidth * (scale || 1)
      finalScale = undefined
      finalHeight = undefined
    } else if (pageHeight) {
      finalWidth = undefined
      finalScale = undefined
      finalHeight = pageHeight * (scale || 1)
    }

    return {
      scale: finalScale,
      width: finalWidth,
      height: finalHeight,
    }
  }
}
